/**
 * 提交整改弹窗
 *
 * UI 对齐 web后台.html FixModal：
 *  - 现有媒体（通过详情接口拉取，× 标记软删除）
 *  - 新增整改媒体（点击从团队相册选择，复用 shared/TeamPhotoPicker/Modal）
 *  - 整改说明 textarea（可选）
 *
 * 当前后端 update_status 接口仅接受 remark，媒体编辑信息暂以摘要形式拼入 remark。
 * 媒体管理接口待后端扩展后再迁移。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, message, Modal, Spin } from 'antd'
import {
  CloseOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  PlusOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  AiAnalysisItem,
  CheckListItem,
  CheckMediaItem,
  MediaResourceParam,
  fetchCheckDetail
} from '../api/check'
import MediaLightbox, { detectIsVideo } from '../shared/MediaLightbox'
import { formatRoundLabel } from './utils'
import {
  TeamPhotoPickerModal,
  TeamPhotoPickResult
} from '../shared/TeamPhotoPicker/Modal'

const { TextArea } = Input

interface FixModalProps {
  visible: boolean
  record: CheckListItem | null
  dispatch: (...args: any[]) => any
  /**
   * 可选：现有 AI 分析轮次列表（含 media_list）。
   * 详情页打开时传入已加载的数据；列表页打开时不传，组件自动通过 check_id 拉详情。
   */
  aiAnalysisList?: AiAnalysisItem[]
  submitting?: boolean
  onCancel: () => void
  /**
   * 提交回调。media_resources 仅在 submit_rectify 场景由 picker 选中的整改照片产生；
   * remark 包含用户备注 + 删除媒体明细（删除暂无独立接口字段）。
   */
  onSubmit: (payload: {
    remark: string
    media_resources: MediaResourceParam[]
  }) => void
}

interface FlatMedia extends CheckMediaItem {
  /** 唯一 key：round 序号 + photo_seq */
  key: string
  /** 所属轮次（"第一次提交"…） */
  roundLabel: string
  roundIndex: number
  removed: boolean
}

const flattenMedia = (list: AiAnalysisItem[] | undefined): FlatMedia[] => {
  const flat: FlatMedia[] = []
  ;(list || []).forEach((round, idx) => {
    const label = formatRoundLabel(idx)
    ;(round.media_list || []).forEach((m) => {
      flat.push({
        ...m,
        key: `${idx}:${m.photo_seq}`,
        roundLabel: label,
        roundIndex: idx,
        removed: false
      })
    })
  })
  return flat
}

const FixModal: React.FC<FixModalProps> = ({
  visible,
  record,
  dispatch,
  aiAnalysisList,
  submitting,
  onCancel,
  onSubmit
}) => {
  const [photos, setPhotos] = useState<FlatMedia[]>([])
  const [loading, setLoading] = useState(false)
  const [addedPhotos, setAddedPhotos] = useState<TeamPhotoPickResult[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [note, setNote] = useState('')
  const [previewIndex, setPreviewIndex] = useState(-1)

  // 打开时：优先用外部传入的 aiAnalysisList（详情页已加载）；
  // 否则按 check_id 拉详情（列表页入口）
  useEffect(() => {
    if (!visible) {
      setPhotos([])
      setAddedPhotos([])
      setPickerOpen(false)
      setNote('')
      setPreviewIndex(-1)
      return
    }
    // 外部已注入媒体 → 直接渲染，跳过网络请求
    if (Array.isArray(aiAnalysisList)) {
      setPhotos(flattenMedia(aiAnalysisList))
      setLoading(false)
      return
    }
    if (!record || record.check_id === undefined || record.check_id === null)
      return
    let cancelled = false
    setLoading(true)
    fetchCheckDetail(dispatch, record.check_id)
      .then((detail) => {
        if (cancelled) return
        setPhotos(flattenMedia(detail.ai_analysis_list))
      })
      .catch((e) => {
        if (cancelled) return
        // 不静默失败，便于排查；photos 保持空，UI 显示"暂无现有媒体"
        console.warn('[FixModal] 拉取详情失败：', e)
        setPhotos([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [visible, record && record.check_id, aiAnalysisList, dispatch])

  const removedCount = useMemo(
    () => photos.filter((p) => p.removed).length,
    [photos]
  )

  const togglePhoto = (key: string) => {
    setPhotos((ps) =>
      ps.map((p) => (p.key === key ? { ...p, removed: !p.removed } : p))
    )
  }

  const handlePrev = useCallback(
    () => setPreviewIndex((i) => (i > 0 ? i - 1 : i)),
    []
  )
  const handleNext = useCallback(
    () => setPreviewIndex((i) => (i < photos.length - 1 ? i + 1 : i)),
    [photos.length]
  )

  const handleSubmit = () => {
    // 只要存在任一改变即可提交:新增照片 / 软删除已有照片 / 备注非空
    const hasAdded = addedPhotos.length > 0
    const hasRemoved = photos.some((p) => p.removed)
    const hasNote = note.trim().length > 0
    if (!hasAdded && !hasRemoved && !hasNote) {
      message.warning('请先添加 / 删除整改照片,或填写整改说明')
      return
    }

    // 按接口文档 5.4:
    //   media_resources 是「整改后完整照片列表」= (现有未删) + (新增),
    //   不是只传新增的。后端拿到完整列表后,自己对比上一轮 AI 分析照片
    //   集合算差异,写入 operation_log.detail.operation("新增 X 张,删除 Y 张")。
    //   remark 字段只放用户填的备注,删除明细不再前端拼接。
    const now = dayjs().format('YYYY-MM-DD HH:mm:ss')

    // 1) 现有照片中未被软删除的
    const keptExisting: MediaResourceParam[] = photos
      .filter((p) => !p.removed)
      .map((p) => ({
        photo_seq: p.photo_seq,
        // 文档要求 photo_file 是完整 URL,后端会自己提取 OSS key
        photo_file: p.photo_url || p.photo_file || '',
        photo_content: '',
        media_type: p.media_type,
        photo_time: p.photo_time || now
      }))

    // 2) 用户新增的(picker 选的是已存在网图,photo_time 用当前时间兜底)
    const newlyAdded: MediaResourceParam[] = addedPhotos.map((p) => ({
      photo_seq: p.seq,
      photo_file: p.url,
      photo_content: '',
      media_type: 0,
      photo_time: now
    }))

    const media_resources = [...keptExisting, ...newlyAdded]

    // 后端协议:submit_rectify 必须有 media_resources(整改后总照片数 > 0)。
    // 全删 + 没新增 → 前端拦下,提示用户至少保留或添加 1 张。
    if (media_resources.length === 0) {
      message.warning('整改后至少保留或添加 1 张照片')
      return
    }

    onSubmit({ remark: note.trim(), media_resources })
  }

  const handlePickerConfirm = useCallback((picked: TeamPhotoPickResult[]) => {
    // 用 seq 去重并合并到已添加列表
    setAddedPhotos((prev) => {
      const seen = new Set(prev.map((p) => p.seq))
      const fresh = picked.filter((p) => !seen.has(p.seq))
      return [...prev, ...fresh]
    })
    setPickerOpen(false)
  }, [])

  const removeAddedPhoto = useCallback((seq: number) => {
    setAddedPhotos((prev) => prev.filter((p) => p.seq !== seq))
  }, [])

  return (
    <Modal
      title="提交整改"
      open={visible}
      width={620}
      destroyOnHidden
      mask={{ closable: false }}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button
          key="ok"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
        >
          确认
        </Button>
      ]}
    >
      {record && (
        <div
          style={{
            background: '#F9FAFB',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 16,
            fontSize: 13,
            color: '#6B7280'
          }}
        >
          <span style={{ color: '#9CA3AF', marginRight: 6 }}>当前记录：</span>
          <span style={{ color: '#1F2937', fontWeight: 500 }}>
            {record.inspection}
          </span>
          <span style={{ color: '#D9D9D9', margin: '0 6px' }}>·</span>
          <span style={{ color: '#3B7DEB', fontFamily: 'Inter, system-ui' }}>
            {record.project_number}
          </span>
        </div>
      )}

      {/* 现有媒体 */}
      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 4
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
            现有媒体
          </span>
          {photos.length > 0 && (
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>
              共 {photos.length} 项
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 12,
            color: '#9CA3AF',
            marginBottom: 10
          }}
        >
          点击 × 标记删除（软删除，提交后记录到操作记录，可恢复）
        </div>
        {loading ? (
          <div
            style={{
              padding: '24px 0',
              textAlign: 'center',
              background: '#FAFAFA',
              borderRadius: 6
            }}
          >
            <Spin size="small" />
          </div>
        ) : photos.length === 0 ? (
          <div
            style={{
              padding: '18px 0',
              textAlign: 'center',
              color: '#9CA3AF',
              fontSize: 12,
              background: '#FAFAFA',
              borderRadius: 6
            }}
          >
            暂无现有媒体
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              paddingTop: 8,
              paddingRight: 8
            }}
          >
            {photos.map((p, i) => {
              const isVideo = detectIsVideo(p)
              return (
                <div
                  key={p.key}
                  style={{
                    position: 'relative',
                    width: 120,
                    height: 90,
                    opacity: p.removed ? 0.5 : 1,
                    transition: 'opacity .15s'
                  }}
                >
                  {/* 内层：负责 overflow:hidden + 点击预览 */}
                  <div
                    onClick={() => setPreviewIndex(i)}
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 6,
                      overflow: 'hidden',
                      background: '#1F252F',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    {isVideo ? (
                      <>
                        <video
                          src={p.photo_url}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          preload="metadata"
                        />
                        <span
                          style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0,0,0,0.2)',
                            color: '#fff',
                            pointerEvents: 'none'
                          }}
                        >
                          <PlayCircleOutlined style={{ fontSize: 24 }} />
                        </span>
                      </>
                    ) : (
                      <img
                        src={p.photo_url}
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    {/* 轮次小标签 */}
                    <span
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        fontSize: 10,
                        color: '#fff',
                        background: 'rgba(0,0,0,0.55)',
                        padding: '1px 6px',
                        borderRadius: 3,
                        lineHeight: '14px'
                      }}
                    >
                      {p.roundLabel}
                    </span>
                    {p.removed && (
                      <span
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: 0,
                          padding: '2px 4px',
                          background: 'rgba(232,76,76,.85)',
                          color: '#fff',
                          fontSize: 11,
                          textAlign: 'center'
                        }}
                      >
                        待删除
                      </span>
                    )}
                  </div>
                  {/* × 切换软删除（放在外层，避开 overflow:hidden 裁剪） */}
                  <span
                    onClick={(e) => {
                      e.stopPropagation()
                      togglePhoto(p.key)
                    }}
                    style={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: p.removed ? '#9CA3AF' : '#E84C4C',
                      color: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      cursor: 'pointer',
                      border: '2px solid #fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                      zIndex: 2
                    }}
                  >
                    {p.removed ? (
                      <PlusOutlined style={{ fontSize: 11 }} />
                    ) : (
                      <CloseOutlined style={{ fontSize: 11 }} />
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        )}
        {removedCount > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#E84C4C' }}>
            将软删除 {removedCount} 张媒体（保留在历史版本中，可在操作记录回溯）
          </div>
        )}
      </div>

      {/* 上传整改文件 — 从团队相册选择 */}
      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 10
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
            上传整改文件
          </span>
          {addedPhotos.length > 0 && (
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>
              已添加 {addedPhotos.length} 张
            </span>
          )}
        </div>
        <div
          onClick={() => setPickerOpen(true)}
          className="myse-dropzone"
          style={{
            border: '1.5px dashed #BFD3F2',
            borderRadius: 8,
            padding: '28px 16px',
            textAlign: 'center',
            cursor: 'pointer',
            background: '#F8FAFD',
            transition: 'all .15s'
          }}
          onMouseEnter={(e) => {
            ;(e.currentTarget as HTMLDivElement).style.borderColor = '#3B7DEB'
            ;(e.currentTarget as HTMLDivElement).style.background = '#EFF6FF'
          }}
          onMouseLeave={(e) => {
            ;(e.currentTarget as HTMLDivElement).style.borderColor = '#BFD3F2'
            ;(e.currentTarget as HTMLDivElement).style.background = '#F8FAFD'
          }}
        >
          <PictureOutlined style={{ fontSize: 26, color: '#3B7DEB' }} />
          <div style={{ fontSize: 13, color: '#3B7DEB', marginTop: 6 }}>
            从团队相册选择
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
            支持照片，最多 30 张
          </div>
        </div>

        {/* 已选缩略图列表 */}
        {addedPhotos.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              marginTop: 12,
              paddingTop: 8,
              paddingRight: 8
            }}
          >
            {addedPhotos.map((p) => (
              <div
                key={p.seq}
                style={{ position: 'relative', width: 120, height: 90 }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 6,
                    overflow: 'hidden',
                    background: '#1F252F',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                  }}
                >
                  <img
                    src={p.smallURL || p.url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <span
                  onClick={() => removeAddedPhoto(p.seq)}
                  style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: '#E84C4C',
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    cursor: 'pointer',
                    border: '2px solid #fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                    zIndex: 2
                  }}
                >
                  <CloseOutlined style={{ fontSize: 11 }} />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 整改说明 */}
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 8
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1F2937' }}>
            整改说明
          </span>
          <span style={{ fontSize: 12, color: '#9CA3AF' }}>可选</span>
        </div>
        <TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={4}
          maxLength={500}
          placeholder="请描述整改措施和结果……"
        />
      </div>

      {previewIndex >= 0 && (
        <MediaLightbox
          list={photos}
          index={previewIndex}
          onClose={() => setPreviewIndex(-1)}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}

      {/* 团队相册选图弹窗 */}
      <TeamPhotoPickerModal
        visible={pickerOpen}
        maxCount={30}
        title="从团队相册选择整改照片"
        excludePhotos={addedPhotos.map((p) => ({
          seq: p.seq,
          smallURL: p.smallURL
        }))}
        onConfirm={handlePickerConfirm}
        onCancel={() => setPickerOpen(false)}
      />
    </Modal>
  )
}

export default FixModal
